// ============================================================================
// The flow from a pull request to prod (2026-10-16): every change in flight in
// exactly one lane (ready to merge, in the merge queue, merging, building,
// going to staging, waiting for prod, deploying, live), and what is stuck, in
// words, with the one safe fix that would move it. The owner's evening of merge
// cards that did nothing came from not seeing any of this.
//
// The same model as internal/pipeline (Go), which GET /api/pipeline and Prime's
// MCP `pipeline` tool use; testdata/pipeline/*.json run through both
// (flow.test.ts here). Keep them in step. Pure. lib/pipeline.ts is the other
// pipeline: one ship's stages from its tag to live, drawn by ShipPipeline.
// ============================================================================
import type { AgentRunResource } from '@/kinds/agentrun/apply'
import type { PullRequestResource } from '@/kinds/git/apply'
import type { SuggestionResource } from '@/kinds/suggestion'
import { mergeTarget, repoKey } from './mergeQueue'
import { questionOf } from './waiting'

export type LaneKey =
  'ready' | 'queued' | 'merging' | 'building' | 'staging' | 'waiting' | 'deploying' | 'live'

export const LANES: { key: LaneKey; label: string }[] = [
  { key: 'ready', label: 'Ready to merge' },
  { key: 'queued', label: 'In the merge queue' },
  { key: 'merging', label: 'Merging' },
  { key: 'building', label: 'Building' },
  { key: 'staging', label: 'Going to staging' },
  { key: 'waiting', label: 'Waiting for prod' },
  { key: 'deploying', label: 'Deploying to prod' },
  { key: 'live', label: 'Live on prod' },
]

export type FixKey = 'close_landed' | 'requeue' | 'update_branch' | 'rebuild'

/** The safe fixes and the words on their button. */
export const FIXES: Record<FixKey, string> = {
  close_landed: 'Close it',
  requeue: 'Queue it again',
  update_branch: 'Ask its agent to fix',
  rebuild: 'Build again',
}

const MIN = 60_000
export const MERGING_STUCK = 3 * MIN
export const QUEUED_STUCK = 5 * MIN
export const BUILD_STUCK = 10 * MIN
export const DEPLOY_STUCK = 10 * MIN
export const LANDED_GRACE = 2 * MIN
export const JUST_MERGED = MIN
export const LIVE_FOR = 6 * 60 * MIN
export const LIVE_MAX = 5
/** A lane shows this many rows before "Show the rest". */
export const MAX_LANE_SHOWN = 10

// ---- input --------------------------------------------------------------------------

export interface FlowPR {
  repo: string
  number: number
  title: string
  url?: string
  state: 'open' | 'merged' | 'closed' | string
  draft?: boolean
  mergeable?: boolean | null
  checks?: string
  merged_at?: string
  updated_at?: string
}

export interface FlowAgent {
  id: string
  title: string
  phase: string
  repo: string
  pr?: number
  pr_state?: string
  since?: string
  asked?: string
}

export interface FlowMerge {
  /** queued | merging | updating | failed | merged */
  state: string
  queued_at?: string
  started_at?: string
  reason?: string
  failed_at?: string
  /** the merge batch it is in (its first card's id) and the batch's Merge and ship */
  batch?: string
  ship?: boolean
  /** when its agent was asked to merge main */
  updating_at?: string
}

export interface FlowCard {
  id: string
  repo: string
  number: number
  created_at?: string
  merge?: FlowMerge
}

export interface FlowBuild {
  name: string
  sha: string
  tag: string
  note?: string
  state: string
  started?: string
  finished?: string
  error?: string
}

export interface FlowRelease {
  tag: string
  sha: string
  at: string
}

export interface FlowCommit {
  sha: string
  subject: string
  seen_at?: string
}

export interface FlowPod {
  image: string
  ready?: boolean
  terminating?: boolean
  waiting?: string
}

export interface FlowInput {
  now: string
  repo: string
  prs: FlowPR[]
  agents: FlowAgent[]
  cards: FlowCard[]
  builds: FlowBuild[]
  releases: FlowRelease[]
  prod: string
  staging: string
  /** main's commits prod lacks, newest first; null when they could not be read */
  main: FlowCommit[] | null
  main_at?: string
  pods?: FlowPod[]
}

// ---- output -------------------------------------------------------------------------

export interface Stuck {
  reason: string
  fix?: FixKey
}

export interface FlowItem {
  key: string
  title: string
  repo?: string
  number?: number
  url?: string
  sha?: string
  agent?: string
  agent_title?: string
  card?: string
  /** the merge batch its card is in */
  batch?: string
  since?: string
  note?: string
  stuck?: Stuck
}

export type BatchMemberState = 'merged' | 'merging' | 'updating' | 'queued' | 'failed'

/** One pull request of a merge batch. */
export interface BatchMember {
  key: string
  number: number
  title: string
  state: BatchMemberState
  /** why it is updating or failed */
  reason?: string
}

/** A merge batch in flight (Go's Batch): shown while a member still waits to merge. */
export interface FlowBatch {
  id: string
  repo: string
  ship?: boolean
  summary: string
  members: BatchMember[]
}

export interface FlowLane {
  key: LaneKey
  label: string
  items: FlowItem[]
}

export interface Flow {
  lanes: FlowLane[]
  /** the build tag Ship all releases */
  ship?: string
  /** the merge batches in flight, oldest first */
  batches?: FlowBatch[]
}

// ---- helpers ------------------------------------------------------------------------

const PR_SUFFIX = /\s*\(#(\d+)\)\s*$/

/** The pull request a squash commit's subject names (" (#N)" at the end), or 0. */
export const prOf = (subject: string): number => {
  const m = PR_SUFFIX.exec(subject)
  return m ? Number(m[1]) : 0
}

/** A commit's subject without its " (#N)". */
export const titleOf = (subject: string) => subject.replace(PR_SUFFIX, '').trim()

/** Whether an environment's version (a sha prefix, or a tag starting with the short sha) is this commit. */
export const runs = (version: string, sha: string): boolean => {
  if (!version || !sha) return false
  if (version.length >= 7 && sha.startsWith(version)) return true
  const short = sha.slice(0, 12)
  return short.length >= 7 && version.startsWith(short)
}

const sameSHA = (a: string, b: string) => {
  const n = Math.min(a.length, b.length)
  return n >= 7 && a.slice(0, n) === b.slice(0, n)
}

const short = (sha: string) => sha.slice(0, 12)
const short7 = (sha: string) => sha.slice(0, 7)

const parseAt = (s: string | undefined): number | null => {
  if (!s) return null
  const t = Date.parse(s)
  return Number.isNaN(t) ? null : t
}

/** Whole minutes from `at` to now (never below 0), or -1 when `at` is unknown. */
export const minutesSince = (at: string | undefined, now: number): number => {
  const t = parseAt(at)
  if (t === null) return -1
  return Math.max(0, Math.floor((now - t) / MIN))
}

/** A number of minutes the way a reason says it: "4 min", "2 h". */
export const span = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h`)

const tagOf = (image: string) => {
  const i = image.lastIndexOf(':')
  return i >= 0 && !image.slice(i).includes('/') ? image.slice(i + 1) : ''
}

/** A pod's waiting reason in words. */
export const waitWords = (reason: string) =>
  reason === 'ContainerCreating'
    ? 'pulling the image and starting'
    : reason === 'PodInitializing'
      ? 'starting'
      : reason.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()

const working = (phase: string) => phase === 'Working' || phase === 'Starting' || phase === 'Pending'
const over = (phase: string) => phase === 'Ended' || phase === 'Failed'

export const prKey = (repo: string, n: number) => `pr:${repo}#${n}`
export const commitKey = (sha: string) => `commit:${short(sha)}`

const firstOf = (...s: (string | undefined)[]) => s.find((v) => !!v) ?? ''

const ordinal = (n: number) => {
  const tens = n % 100
  const suffix =
    tens >= 11 && tens <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th'
  return `${n}${suffix}`
}

/** An item with only the fields that carry something (Go's omitempty). */
const item = (it: FlowItem): FlowItem => {
  const out: FlowItem = { key: it.key, title: it.title }
  if (it.repo) out.repo = it.repo
  if (it.number) out.number = it.number
  if (it.url) out.url = it.url
  if (it.sha) out.sha = it.sha
  if (it.agent) out.agent = it.agent
  if (it.agent_title) out.agent_title = it.agent_title
  if (it.card) out.card = it.card
  if (it.batch) out.batch = it.batch
  if (it.since) out.since = it.since
  if (it.note) out.note = it.note
  if (it.stuck) out.stuck = it.stuck.fix ? it.stuck : { reason: it.stuck.reason }
  return out
}

const podWords = (pods: FlowPod[] | undefined, tag: string) => {
  for (const p of pods ?? []) {
    if (tagOf(p.image) !== tag || p.terminating) continue
    if (p.waiting) return `the new pod is waiting: ${waitWords(p.waiting)}`
    if (!p.ready) return 'the new pod is not ready'
    return 'the new pod is ready but prod still answers with the old version'
  }
  return 'no pod runs it yet (Argo CD has not applied it)'
}

const mainFromBuilds = (input: FlowInput): FlowCommit[] => {
  const out: FlowCommit[] = []
  const seen = new Set<string>()
  for (const b of input.builds) {
    if (runs(input.prod, b.sha)) return out
    if (seen.has(b.sha) || !b.sha) continue
    seen.add(b.sha)
    out.push({ sha: b.sha, subject: b.note ?? '', ...(b.started ? { seen_at: b.started } : {}) })
  }
  if (!input.prod || out.length > MAX_LANE_SHOWN) return []
  return out
}

// ---- the model ----------------------------------------------------------------------

/** Every change in flight, each in one lane. */
export const flowOf = (input: FlowInput): Flow => {
  const now = parseAt(input.now) ?? 0
  const lanes = new Map<LaneKey, FlowItem[]>()
  const add = (lane: LaneKey, it: FlowItem) => lanes.set(lane, [...(lanes.get(lane) ?? []), item(it)])

  const mainKnown = input.main !== null
  const main = input.main ?? mainFromBuilds(input)
  const pos = (sha: string) => main.findIndex((c) => sameSHA(c.sha, sha))
  const agentFor = (repo: string, n: number): FlowAgent | undefined => {
    let found: FlowAgent | undefined
    for (const a of input.agents) {
      if (a.pr !== n || a.repo !== repo || n === 0) continue
      if (!found || (over(found.phase) && !over(a.phase))) found = a
    }
    return found
  }

  // ---- before the merge: open pull requests -----------------------------------
  interface P extends FlowPR {
    agent?: FlowAgent
    gone: boolean
  }
  const prs = new Map<string, P>()
  const order: string[] = []
  const get = (repo: string, n: number): P => {
    const k = prKey(repo, n)
    let p = prs.get(k)
    if (!p) {
      p = { repo, number: n, title: '', state: 'open', gone: false }
      prs.set(k, p)
      order.push(k)
    }
    return p
  }
  for (const m of input.prs) {
    if (m.number <= 0 || !m.repo) continue
    const p = get(m.repo, m.number)
    const gone = p.gone
    Object.assign(p, m, { agent: undefined, gone: false })
    p.gone = m.state === 'merged' || m.state === 'closed' || gone
  }
  for (const a of input.agents) {
    if (!a.pr || a.pr <= 0 || !a.repo) continue
    const p = get(a.repo, a.pr)
    if (!p.title) p.title = a.title
    if (a.pr_state === 'merged' || a.pr_state === 'closed') {
      p.gone = true
      if (p.state === 'open') p.state = a.pr_state
    }
  }
  for (const k of order) {
    const p = prs.get(k) as P
    p.agent = agentFor(p.repo, p.number)
  }
  // a card that says merged is believed over a mirror that lags
  for (const c of input.cards) {
    if (c.merge?.state !== 'merged') continue
    const p = prs.get(prKey(c.repo, c.number))
    if (p) p.gone = true
  }

  const landed = new Map<number, { sha: string; at?: string }>()
  for (const c of main) {
    const n = prOf(c.subject)
    if (n > 0 && !landed.has(n)) landed.set(n, { sha: c.sha, at: c.seen_at })
  }
  for (const b of input.builds) {
    const n = prOf(b.note ?? '')
    if (n > 0 && !landed.has(n)) landed.set(n, { sha: b.sha, at: b.started })
  }

  const rank = (c: FlowCard) => {
    const st = c.merge?.state
    return st === 'merging' ? 3 : st === 'queued' || st === 'updating' ? 2 : st === 'failed' ? 1 : 0
  }
  const cardFor = (repo: string, n: number): FlowCard | undefined => {
    let found: FlowCard | undefined
    for (const c of input.cards) {
      if (c.repo !== repo || c.number !== n || c.merge?.state === 'merged') continue
      if (!found || rank(c) > rank(found) || (rank(c) === rank(found) && c.id < found.id)) found = c
    }
    return found
  }
  const mergingIn = new Set(input.cards.filter((c) => c.merge?.state === 'merging').map((c) => c.repo))
  const queueLine = (repo: string) =>
    input.cards
      .filter((c) => c.repo === repo && c.merge?.state === 'queued')
      .sort((a, b) => {
        const at = a.merge?.queued_at ?? ''
        const bt = b.merge?.queued_at ?? ''
        if (at !== bt) return at < bt ? -1 : 1
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
      })

  for (const k of order) {
    const p = prs.get(k) as P
    if (p.gone || p.draft) continue
    if (p.repo !== input.repo && !p.agent) continue
    const card = cardFor(p.repo, p.number)
    const it: FlowItem = {
      key: k,
      title: p.title,
      repo: p.repo,
      number: p.number,
      url: p.url,
      agent: p.agent?.id,
      agent_title: p.agent?.title,
      card: card?.id,
      batch: card?.merge?.batch,
    }
    const land = landed.get(p.number)
    let isLanded = !!land && p.repo === input.repo
    if (isLanded) {
      const m = minutesSince(land?.at, now)
      if (m >= 0 && m < LANDED_GRACE / MIN) isLanded = false
    }
    let lane: LaneKey = 'ready'
    const mstate = card?.merge?.state ?? ''
    if (mstate === 'merging') {
      lane = 'merging'
      it.since = firstOf(card?.merge?.started_at, card?.merge?.queued_at)
    } else if (mstate === 'updating') {
      // out of the line while its agent merges main; the queue merges it when
      // the head moves, or fails it
      lane = 'queued'
      it.since = firstOf(card?.merge?.updating_at, card?.merge?.queued_at)
      it.note = 'Its agent is merging main into it'
    } else if (mstate === 'queued') {
      lane = 'queued'
      it.since = card?.merge?.queued_at
      queueLine(p.repo).forEach((c, i) => {
        if (c.id === card?.id) it.note = `${ordinal(i + 1)} in line`
      })
    } else {
      if (p.agent && working(p.agent.phase) && !isLanded && mstate === '') continue
      if (mstate === 'failed') it.since = firstOf(card?.merge?.failed_at, card?.created_at)
      else if (p.agent) it.since = firstOf(p.agent.since, p.updated_at)
      else it.since = p.updated_at
    }
    const live = !!p.agent && !over(p.agent.phase)
    const fixIfAgent = (fix: FixKey): FixKey | undefined => (live ? fix : undefined)
    const sinceMin = minutesSince(it.since, now)
    if (isLanded) {
      it.stuck = {
        reason: `Its change is already on main (${short7(land?.sha ?? '')}) but Forgejo still shows the pull request open`,
        fix: 'close_landed',
      }
    } else if (mstate === 'merging' && sinceMin >= MERGING_STUCK / MIN) {
      it.stuck = { reason: `Merging for ${span(sinceMin)}; a merge takes under two minutes`, fix: 'requeue' }
    } else if (mstate === 'queued' && !mergingIn.has(p.repo) && sinceMin >= QUEUED_STUCK / MIN) {
      it.stuck = {
        reason: `Queued ${span(sinceMin)} and nothing is merging: the queue has not picked it up`,
        fix: 'requeue',
      }
    } else if (mstate === 'failed') {
      const reason = (card?.merge?.reason ?? '').trim() || 'The merge did not go through'
      it.stuck = { reason, fix: p.mergeable === false && live ? 'update_branch' : 'requeue' }
    } else if (mstate === '' && p.mergeable === false) {
      it.stuck = {
        reason: live
          ? 'It conflicts with main'
          : 'It conflicts with main; its author has to merge main into it',
        fix: fixIfAgent('update_branch'),
      }
    } else if (mstate === '' && p.checks === 'failure') {
      it.stuck = { reason: 'Its checks failed', fix: fixIfAgent('update_branch') }
    } else if (mstate === '' && p.agent?.asked) {
      it.stuck = { reason: `Its agent asked you: ${p.agent.asked}` }
    }
    add(lane, it)
  }

  // ---- after the merge: main's commits that prod lacks ------------------------
  const rel = input.releases[0]
  const relPos = rel && !runs(input.prod, rel.sha) ? pos(rel.sha) : -1
  const stPos = main.findIndex((c) => runs(input.staging, c.sha))
  const buildPos = input.builds.map((b) => pos(b.sha))
  const inMain = new Set<number>()
  main.forEach((c, i) => {
    const n = prOf(c.subject)
    if (n > 0) inMain.add(n)
    const it: FlowItem = {
      key: commitKey(c.sha),
      title: titleOf(c.subject) || short7(c.sha),
      repo: input.repo,
      number: n,
      sha: c.sha,
    }
    if (n > 0) {
      const a = agentFor(input.repo, n)
      if (a) {
        it.agent = a.id
        it.agent_title = a.title
      }
      const p = prs.get(prKey(input.repo, n))
      if (p?.url) it.url = p.url
    }
    let built: FlowBuild | undefined
    let going: FlowBuild | undefined
    let failed: FlowBuild | undefined
    input.builds.forEach((b, bi) => {
      const bp = buildPos[bi] ?? -1
      if (bp < 0 || bp > i) return
      if (b.state === 'built') built ??= b
      else if (b.state === 'queued' || b.state === 'building') going ??= b
      else if (b.state === 'failed') failed ??= b
    })
    if (built && rel && relPos >= 0 && relPos <= i) {
      it.since = rel.at
      const m = minutesSince(rel.at, now)
      if (m >= DEPLOY_STUCK / MIN)
        it.stuck = {
          reason: `Tagged for prod ${span(m)} ago and prod is not on it yet; ${podWords(input.pods, rel.tag)}`,
        }
      add('deploying', it)
    } else if (built) {
      it.since = firstOf(built.finished, built.started)
      add(!input.staging || (stPos >= 0 && stPos <= i) ? 'waiting' : 'staging', it)
    } else if (going) {
      it.since = going.started
      it.note = going.state === 'queued' ? 'Build queued' : 'Building'
      add('building', it)
    } else if (failed) {
      it.since = firstOf(failed.finished, failed.started)
      it.note = 'Build failed'
      it.stuck = {
        reason: `Build ${failed.tag} failed: ${(failed.error ?? '').trim() || 'see its log'}`,
        fix: 'rebuild',
      }
      add('building', it)
    } else {
      it.since = c.seen_at
      it.note = 'Not built yet'
      const m = minutesSince(c.seen_at, now)
      if (m >= BUILD_STUCK / MIN)
        it.stuck = { reason: `Not built ${span(m)} after it reached main`, fix: 'rebuild' }
      add('building', it)
    }
  })

  // ---- merged pull requests not on main's list: just merged, or live ------------
  if (mainKnown) {
    const mainAt = parseAt(input.main_at)
    const live: FlowItem[] = []
    for (const k of order) {
      const p = prs.get(k) as P
      if (p.repo !== input.repo || p.state !== 'merged' || inMain.has(p.number)) continue
      const at = parseAt(p.merged_at)
      if (at === null) continue
      const it: FlowItem = {
        key: k,
        title: p.title,
        repo: p.repo,
        number: p.number,
        url: p.url,
        since: p.merged_at,
        agent: p.agent?.id,
        agent_title: p.agent?.title,
      }
      if (mainAt !== null && at >= mainAt - JUST_MERGED) {
        it.note = 'Just merged'
        add('building', it)
        continue
      }
      if (now - at > LIVE_FOR) continue
      live.push(it)
    }
    live
      .sort((a, b) => {
        const as = a.since ?? ''
        const bs = b.since ?? ''
        if (as !== bs) return as > bs ? -1 : 1
        return (b.number ?? 0) - (a.number ?? 0)
      })
      .slice(0, LIVE_MAX)
      .forEach((it) => add('live', it))
  }

  // ---- order within a lane ------------------------------------------------------
  const ours = input.repo
  lanes.get('ready')?.sort((a, b) => {
    if (a.repo !== b.repo) {
      if (a.repo === ours) return -1
      if (b.repo === ours) return 1
      return (a.repo ?? '') < (b.repo ?? '') ? -1 : 1
    }
    return (a.number ?? 0) - (b.number ?? 0)
  })
  lanes.get('queued')?.sort((a, b) => {
    const as = a.since ?? ''
    const bs = b.since ?? ''
    if (as !== bs) return as < bs ? -1 : 1
    const ac = a.card ?? ''
    const bc = b.card ?? ''
    return ac < bc ? -1 : ac > bc ? 1 : 0
  })
  lanes.get('merging')?.sort((a, b) => {
    const as = a.since ?? ''
    const bs = b.since ?? ''
    return as < bs ? -1 : as > bs ? 1 : 0
  })

  const out: Flow = {
    lanes: LANES.map((l) => ({ key: l.key, label: l.label, items: lanes.get(l.key) ?? [] })),
  }
  const batches = batchesOf(input, (repo, n) => prs.get(prKey(repo, n))?.title || `PR #${n}`)
  if (batches.length > 0) out.batches = batches
  const firstBuilt = input.builds.findIndex((b) => b.state === 'built')
  if (firstBuilt >= 0) {
    const bp = buildPos[firstBuilt] ?? -1
    if (bp >= 0 && (relPos < 0 || relPos > bp)) out.ship = input.builds[firstBuilt]?.tag
  }
  return out
}

// ---- merge batches (Go's internal/pipeline/batch.go) --------------------------------

const MEMBER_RANK: Record<BatchMemberState, number> = {
  merged: 0,
  merging: 1,
  updating: 2,
  queued: 3,
  failed: 4,
}
const isMemberState = (s: string): s is BatchMemberState => s in MEMBER_RANK

/** A batch in one line: "Merging 6: 3 in, 1 updating, 2 waiting". */
export const batchWords = (members: BatchMember[], ship: boolean): string => {
  const n = (st: BatchMemberState) => members.filter((m) => m.state === st).length
  const parts = (
    [
      ['merged', 'in'],
      ['merging', 'merging'],
      ['updating', 'updating'],
      ['queued', 'waiting'],
      ['failed', 'failed'],
    ] as const
  )
    .filter(([st]) => n(st) > 0)
    .map(([st, word]) => `${n(st)} ${word}`)
  return `${ship ? 'Merging and shipping' : 'Merging'} ${members.length}: ${parts.join(', ')}`
}

/**
 * The cards grouped by batch: a batch shows while a member is queued, merging
 * or updating, and only when it is a batch (two pull requests or more, or one
 * with Merge and ship). Oldest batch first.
 */
const batchesOf = (input: FlowInput, title: (repo: string, n: number) => string): FlowBatch[] => {
  interface Group {
    b: FlowBatch
    first: string
    byPR: Map<number, number>
    ats: Map<number, string>
    live: boolean
  }
  const groups = new Map<string, Group>()
  for (const c of input.cards) {
    const m = c.merge
    if (!m?.batch || c.number <= 0) continue
    let g = groups.get(m.batch)
    if (!g) {
      g = {
        b: { id: m.batch, repo: c.repo, summary: '', members: [] },
        first: '',
        byPR: new Map(),
        ats: new Map(),
        live: false,
      }
      groups.set(m.batch, g)
    }
    if (m.ship) g.b.ship = true
    const at = m.queued_at ?? ''
    if (!g.first || at < g.first) g.first = at
    if (!isMemberState(m.state)) continue
    const state = m.state
    if (state === 'queued' || state === 'merging' || state === 'updating') g.live = true
    const mem: BatchMember = {
      key: prKey(c.repo, c.number),
      number: c.number,
      title: title(c.repo, c.number),
      state,
    }
    const reason = (m.reason ?? '').trim()
    if ((state === 'updating' || state === 'failed') && reason) mem.reason = reason
    // two cards for one pull request are one member: the one furthest along
    const i = g.byPR.get(c.number)
    if (i !== undefined) {
      const had = g.b.members[i]
      if (had && MEMBER_RANK[state] < MEMBER_RANK[had.state]) {
        g.b.members[i] = mem
        g.ats.set(c.number, at)
      }
      continue
    }
    g.byPR.set(c.number, g.b.members.length)
    g.b.members.push(mem)
    g.ats.set(c.number, at)
  }
  const out: { b: FlowBatch; first: string }[] = []
  for (const g of groups.values()) {
    if (!g.live || (g.b.members.length < 2 && !g.b.ship)) continue
    g.b.members.sort((a, b) => {
      if (MEMBER_RANK[a.state] !== MEMBER_RANK[b.state]) return MEMBER_RANK[a.state] - MEMBER_RANK[b.state]
      const aa = g.ats.get(a.number) ?? ''
      const ba = g.ats.get(b.number) ?? ''
      if (aa !== ba) return aa < ba ? -1 : 1
      return a.number - b.number
    })
    g.b.summary = batchWords(g.b.members, !!g.b.ship)
    out.push({ b: g.b, first: g.first })
  }
  out.sort((x, y) =>
    x.first !== y.first ? (x.first < y.first ? -1 : 1) : x.b.id < y.b.id ? -1 : x.b.id > y.b.id ? 1 : 0,
  )
  return out.map((x) => x.b)
}

/** One lane's items. */
export const laneOf = (f: Flow, key: LaneKey): FlowItem[] => f.lanes.find((l) => l.key === key)?.items ?? []

/** Whether anything is moving, so the screen asks again soon. */
export const inMotion = (f: Flow): boolean =>
  ['queued', 'merging', 'building', 'staging', 'deploying'].some((k) => laneOf(f, k as LaneKey).length > 0)

/** The ready pull requests "Merge all" queues, in order: nothing stuck, nothing already queued. */
export const mergeable = (f: Flow): FlowItem[] => laneOf(f, 'ready').filter((it) => !it.stuck && !!it.number)

// ---- from what the device holds --------------------------------------------------------

const repoOfURL = (url: string) => repoKey(url.replace(/^https?:\/\/[^/]+\//, ''))

/** A coding agent (never Prime) as the model reads it. */
export const flowAgent = (r: AgentRunResource, phase = r.spec.status): FlowAgent | null => {
  if (r.deleted_at || r.spec.template === 'prime' || r.spec.source.provider !== 'factory') return null
  const a: FlowAgent = {
    id: r.id,
    title: r.spec.title || r.spec.name || 'An agent',
    phase,
    repo: repoOfURL(r.spec.repo_url),
  }
  if (r.spec.pr_number) a.pr = r.spec.pr_number
  if (r.spec.pr_state) a.pr_state = r.spec.pr_state
  const since = r.spec.last_active_at || r.spec.updated_at
  if (since) a.since = since
  const asked = questionOf(r)
  if (asked) a.asked = asked
  return a
}

/** A pull request mirror as the model reads it. */
export const flowPR = (r: PullRequestResource): FlowPR | null => {
  if (r.deleted_at) return null
  const s = r.spec
  const p: FlowPR = { repo: repoKey(s.repo), number: s.number, title: s.title, state: s.state }
  if (s.source.url) p.url = s.source.url
  if (s.draft) p.draft = true
  if (s.mergeable !== null && s.mergeable !== undefined) p.mergeable = s.mergeable
  if (s.checks) p.checks = s.checks
  if (s.merged_at) p.merged_at = s.merged_at
  if (s.source.updated_at) p.updated_at = s.source.updated_at
  return p
}

/**
 * A card whose only work is merging one pull request, as the model reads it:
 * pending, or merged in a batch (the batch counts it as in).
 */
export const flowCard = (s: SuggestionResource): FlowCard | null => {
  if (s.deleted_at) return null
  const mergedInBatch =
    s.status.state === 'accepted' && s.status.merge?.state === 'merged' && !!s.status.merge.batch
  if (s.status.state !== 'pending' && !mergedInBatch) return null
  const t = mergeTarget(s)
  if (!t) return null
  const c: FlowCard = { id: s.id, repo: t.repo, number: t.number, created_at: s.created_at }
  const m = s.status.merge
  if (m?.state) {
    const merge: FlowMerge = { state: m.state }
    if (m.queued_at) merge.queued_at = m.queued_at
    if (m.started_at) merge.started_at = m.started_at
    if (m.reason) merge.reason = m.reason
    if (m.failed_at) merge.failed_at = m.failed_at
    if (m.batch) merge.batch = m.batch
    if (m.ship) merge.ship = true
    if (m.updating_at) merge.updating_at = m.updating_at
    c.merge = merge
  }
  return c
}
